import {
  createCourseLine,
  CreateCourseLine,
  UpdateCourseLine,
  updateCourseLine,
} from '@/data/services/courseLineServices';
import { CourseLine } from '@/models/CourseLine';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import CourseLineForm, { CourseLineFormFields } from './CourseLineForm';
import useCourseLineWorkspace from '@/data/hook/course-line/useCourseLineWorkspace';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { generateBannerPayload } from '@/utils/courses';

export type CourseLineFormServiceSuccess = {
  item: CourseLine;
  action: 'update' | 'create';
};

type CourseLineFormServiceProps = {
  onSuccess?: (success: CourseLineFormServiceSuccess) => void;
};

export default function CourseLineFormService({
  onSuccess,
}: CourseLineFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.alerts',
  });
  const { courseLine, cache } = useCourseLineWorkspace();

  const handleCreate = async (data: CourseLineFormFields) => {
    const bannerPayload = await generateBannerPayload(data.bannerUrl);
    const payload: CreateCourseLine = {
      name: data.name,
      category: data.category,
      subcategory: data.subcategory,
      ...bannerPayload,
    };
    const newCourseLine = await createCourseLine(payload);
    alert.success(t('createSuccess'));
    return newCourseLine;
  };

  const handleUpdate = async (data: CourseLineFormFields) => {
    if (!courseLine) throw new Error('CourseLine not created');
    const bannerPayload = await generateBannerPayload(data.bannerUrl);
    const payload: UpdateCourseLine = {
      name: data.name,
      category: data.category,
      subcategory: data.subcategory,
      ...bannerPayload,
    };
    const changedFields = buildChangedObject(courseLine, payload);
    if (Object.keys(changedFields).length === 0) return courseLine;
    const updatedCourseLine = await updateCourseLine(
      courseLine.id,
      changedFields,
    );
    alert.success(t('updateSuccess'));
    return updatedCourseLine;
  };

  const handleSubmit = async (
    data: CourseLineFormFields,
  ): Promise<CourseLineFormServiceSuccess> => {
    if (!courseLine) {
      const newCourseLine = await handleCreate(data);
      return { item: newCourseLine, action: 'create' };
    } else {
      const updatedCourseLine = await handleUpdate(data);
      return { item: updatedCourseLine, action: 'update' };
    }
  };

  const { mutate: onSubmit, isPending } = useMutation(handleSubmit, {
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async response => {
      cache?.merge(response.item);
      onSuccess?.(response);
    },
  });

  return (
    <CourseLineForm
      courseLine={courseLine}
      onSubmit={onSubmit}
      isLoading={isPending}
    />
  );
}
